"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Field,
  FieldContent,
  FieldGroup,
  FieldLabel,
  FieldDescription,
} from "@/components/ui/field";
import {
  AlertCircleIcon,
  Calendar1Icon,
  FileTextIcon,
  InfoIcon,
} from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";
import { formatProductionWeekLabel } from "@/lib/cat/format-production-week";
import { Kvps } from "@/lib/kvps";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { Skeleton } from "@/components/ui/skeleton";
import { useLemaSalesPersonsQuery } from "@/lib/queries";
import type { GetLemaSalesPersonsResponse } from "@/app/api/lema-sales-persons/route";
import {
  getLemaSalesPersonsQueryParamsFromKvpsIdentifier,
  isLemaSalesPersonsFilterActive,
  type LemaSalesPersonsQueryParams,
} from "@/lib/cat/lema-sales-persons-query-params";
import { useVehicleAssignmentStepError } from "@/components/backoffice/cat/vehicle-assignment/vehicle-assignment-step-error";
import { useCurrentDraftStore } from "@/stores/selected-vehicle-store";
import { useRetailVehicleAssignmentStore } from "@/stores/mulesoft-vehicle-data-Store";
import { saveDraftData } from "@/app/lib/actions/cat/additionnal-informations";
import { max, setISOWeek, setISOWeekYear, startOfISOWeek } from "date-fns";
type SaveAdditionalInformationsDraftParams = Parameters<
  typeof saveDraftData
>[0];

interface AdditionalInfoProps {
  onCanProceedChange?: (canProceed: boolean) => void;
  currentKvps?: Kvps | null;
  onBeforeNextChange?: (
    handler: (() => boolean | void | Promise<boolean | void>) | null,
  ) => void;
}

type LemaSellerRow = GetLemaSalesPersonsResponse["lemaSalesPersons"][number];

function formatLemaSellerLabel(
  row: Pick<LemaSellerRow, "firstname" | "lastname" | "lemaId">,
) {
  const first = (row.firstname ?? "").trim();
  const last = (row.lastname ?? "").trim();
  const lema = (row.lemaId ?? "").trim();
  const fullName = `${first} ${last}`.trim();
  return `${fullName} (${lema})`;
}

const missingVehicleKvpsMessage =
  "Le KVPS du véhicule est indisponible. Revenez à l'étape d'identification du véhicule.";

const PRODUCTION_WEEK_CODE_REGEX = /^(\d{4})W(\d{2})$/i;

/** Monday of the ISO week in `productionDate` (format `YYYYW{week}`, e.g. `2026W02`). */
function getFirstDayOfProductionWeek(
  productionDate: string | undefined | null,
): Date | undefined {
  if (!productionDate?.trim()) return undefined;

  const match = PRODUCTION_WEEK_CODE_REGEX.exec(productionDate.trim());
  if (!match) return undefined;

  const year = Number(match[1]);
  const week = Number(match[2]);

  if (
    !Number.isInteger(year) ||
    year < 1000 ||
    year > 9999 ||
    !Number.isInteger(week) ||
    week < 1 ||
    week > 53
  ) {
    return undefined;
  }

  const anchor = new Date(year, 0, 4);
  const dateInWeek = setISOWeek(setISOWeekYear(anchor, year), week);
  return startOfISOWeek(dateInWeek);
}

/** Earliest allowed preferred delivery date: later of production week start and order signature. */
function getPreferredDeliveryMinDate(
  firstDayOfProductionWeek: Date | undefined,
): Date | undefined {
  const today = new Date();

  return firstDayOfProductionWeek
    ? max([firstDayOfProductionWeek, today])
    : today;
}

export function RetailAdditionalInformationsStep({
  onCanProceedChange,
  currentKvps,
  onBeforeNextChange,
}: AdditionalInfoProps) {
  const { showStepError } = useVehicleAssignmentStepError();
  const currentDraft = useCurrentDraftStore((state) => state.currentDraft);

  const [signatureDate, setSignatureDate] = useState(
    currentDraft?.signatureDate || "",
  );
  const [preferredDeliveryDate, setPreferredDeliveryDate] = useState(
    currentDraft?.preferredDeliveryDate || "",
  );
  const [maxDeliveryDate, setMaxDeliveryDate] = useState(
    currentDraft?.maxDeliveryDate || "",
  ); // format "23/2026"
  const [salesPersonId, setSalesPersonId] = useState<
    LemaSellerRow["id"] | null
  >(currentDraft?.salesPersonId ?? null);
  const [partnerOffersRefusal, setPartnerOffersRefusal] = useState(
    currentDraft?.partnerOffersRefusal || false,
  );
  const [vgfOffersConsent, setVgfOffersConsent] = useState(
    currentDraft?.vgfOffersConsent || false,
  );
  const setCurrentDraft = useCurrentDraftStore(
    (state) => state.setCurrentDraft,
  );
  const muleSoftVehicleData = useRetailVehicleAssignmentStore(
    (state) => state.muleSoftVehicleData,
  );

  const firstDayOfProductionWeek = useMemo(
    () => getFirstDayOfProductionWeek(muleSoftVehicleData?.productionDate),
    [muleSoftVehicleData?.productionDate],
  );

  const preferredDeliveryMinDate = useMemo(
    () => getPreferredDeliveryMinDate(firstDayOfProductionWeek),
    [firstDayOfProductionWeek],
  );

  const productionWeekLabel = useMemo(
    () => formatProductionWeekLabel(muleSoftVehicleData?.productionDate),
    [muleSoftVehicleData?.productionDate],
  );

  const vehicleKvpsIdentifier =
    currentDraft?.kvps ?? currentKvps?.identifier ?? null;

  const sellerQueryParams = useMemo(
    (): LemaSalesPersonsQueryParams =>
      getLemaSalesPersonsQueryParamsFromKvpsIdentifier(vehicleKvpsIdentifier),
    [vehicleKvpsIdentifier],
  );

  const sellerFilterActive = isLemaSalesPersonsFilterActive(sellerQueryParams);

  const lemaSalesPersonsQuery = useLemaSalesPersonsQuery(
    sellerQueryParams,
    undefined,
    { enabled: sellerFilterActive },
  );

  const sellerOptions = useMemo(
    () => lemaSalesPersonsQuery.data?.lemaSalesPersons ?? [],
    [lemaSalesPersonsQuery.data?.lemaSalesPersons],
  );
  const salesPerson = useMemo(
    () => sellerOptions.find((person) => person.id === salesPersonId) ?? null,
    [salesPersonId, sellerOptions],
  );

  const resolvedSalesPersonId = salesPerson?.id ?? null;

  const showSellerQueryError = lemaSalesPersonsQuery.isError;

  const showMissingVehicleKvps = !sellerFilterActive;

  /** Empty table or API returned no rows (avoid relying only on `total`, which may be absent on error payloads). */
  const showSellerEmptyList =
    sellerFilterActive &&
    lemaSalesPersonsQuery.isSuccess &&
    sellerOptions.length === 0;

  const emptySellerMessageKvps =
    "Aucun vendeur disponible pour ce KVPS. Contactez le service de référentiel LEMA pour ajouter ou mettre à jour les vendeurs dans l’annuaire.";

  const emptySellerMessageRepositoryEmpty =
    "Aucun vendeur n’est présent dans l’annuaire applicatif. Contactez les services techniques de l’application.";

  const totalInDatabase = lemaSalesPersonsQuery.data?.totalInDatabase;

  const emptySellerAlertMessage =
    totalInDatabase === undefined
      ? sellerFilterActive
        ? emptySellerMessageKvps
        : emptySellerMessageRepositoryEmpty
      : totalInDatabase === 0
        ? emptySellerMessageRepositoryEmpty
        : sellerFilterActive
          ? emptySellerMessageKvps
          : emptySellerMessageRepositoryEmpty;

  useEffect(() => {
    onCanProceedChange?.(
      !!signatureDate && !!preferredDeliveryDate && !!salesPerson,
    );
  }, [signatureDate, preferredDeliveryDate, salesPerson, onCanProceedChange]);

  const handleBeforeNext = useCallback(async () => {
    if (currentDraft === null) {
      showStepError("Aucun brouillon associé à cette affectation.");
      return false;
    }

    const params: SaveAdditionalInformationsDraftParams = {
      id: currentDraft.id,
      signatureDate: new Date(signatureDate),
      preferredDeliveryDate: new Date(preferredDeliveryDate),
      maxDeliveryDate: new Date(maxDeliveryDate),
      salesPersonId: resolvedSalesPersonId,
      partnerOffersRefusal,
      vgfOffersConsent,
    };

    const result = await saveDraftData(params);

    if (!result.success) {
      showStepError(
        result.message ??
          "Une erreur est survenue lors de l'enregistrement du véhicule.",
      );
      return false;
    }

    if (!result.data) {
      showStepError(
        "Aucune donnée client n'a été retournée après l'enregistrement.",
      );
      return false;
    }

    setCurrentDraft(result.data);

    return true;
  }, [
    currentDraft,
    maxDeliveryDate,
    partnerOffersRefusal,
    preferredDeliveryDate,
    resolvedSalesPersonId,
    setCurrentDraft,
    showStepError,
    signatureDate,
    vgfOffersConsent,
  ]);

  useEffect(() => {
    onBeforeNextChange?.(handleBeforeNext);
    return () => onBeforeNextChange?.(null);
  }, [handleBeforeNext, onBeforeNextChange]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="space-y-3 pb-2">
          <CardTitle className="flex items-center space-x-2">
            <FileTextIcon className="h-5 w-5 text-[#005EA8]" />
            <span>Informations Complémentaires</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 pt-2">
          {/* Dates */}
          <div className="space-y-4">
            <Label className="flex items-center space-x-2 text-base font-semibold">
              <span>Dates importantes</span>
            </Label>

            <FieldGroup className="grid grid-cols-3 gap-4 md:grid-cols-3">
              <Field>
                <FieldLabel htmlFor="signatureDate">
                  Date du bon de commande *
                </FieldLabel>
                <DatePicker
                  id="signatureDate"
                  value={signatureDate ? new Date(signatureDate) : undefined}
                  onChange={(e) => {
                    setSignatureDate(e?.toString() || "");
                    setPreferredDeliveryDate("");
                    setMaxDeliveryDate("");
                  }}
                  max={new Date()}
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="preferredDeliveryDate">
                  Date de livraison souhaitée *
                </FieldLabel>
                <DatePicker
                  id="preferredDeliveryDate"
                  value={
                    preferredDeliveryDate
                      ? new Date(preferredDeliveryDate)
                      : undefined
                  }
                  onChange={(e) => {
                    setPreferredDeliveryDate(e?.toString() || "");
                    setMaxDeliveryDate("");
                  }}
                  min={preferredDeliveryMinDate}
                  disabled={!signatureDate}
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="maxDeliveryDate">
                  Date extrême de livraison *
                </FieldLabel>
                <DatePicker
                  id="maxDeliveryDate"
                  value={
                    maxDeliveryDate ? new Date(maxDeliveryDate) : undefined
                  }
                  onChange={(e) => setMaxDeliveryDate(e?.toString() || "")}
                  min={new Date(preferredDeliveryDate)}
                  disabled={!preferredDeliveryDate}
                />
              </Field>
            </FieldGroup>

            <Alert>
              <InfoIcon className="h-4 w-4" />
              <AlertDescription>
                {
                  "Ces dates permettent de planifier la livraison et d'anticiper les contraintes logistiques."
                }
              </AlertDescription>
            </Alert>
          </div>

          {/* Consent */}
          <div className="space-y-4 border-t pt-4">
            <Label className="flex items-center space-x-2 text-base font-semibold">
              <span>Consentements</span>
            </Label>

            <FieldGroup className="flex gap-4">
              <Field orientation="horizontal">
                <Checkbox
                  id="vgfOffersConsent"
                  checked={vgfOffersConsent === true}
                  onCheckedChange={(checked) =>
                    setVgfOffersConsent(checked === true)
                  }
                />
                <FieldLabel htmlFor="vgfOffersConsent">
                  {
                    "J’accepte de recevoir des offres commerciales, y compris des offres personnalisées, relatives aux produits et services commercialisés par Volkswagen Group France, Volkswagen Bank Gmbh, et Volkswagen Financial Services S.A."
                  }
                </FieldLabel>
              </Field>

              <Field orientation="horizontal">
                <Checkbox
                  id="partnerOffersRefusal"
                  checked={partnerOffersRefusal === true}
                  onCheckedChange={(checked) =>
                    setPartnerOffersRefusal(checked === true)
                  }
                />
                <FieldLabel htmlFor="partnerOffersRefusal">
                  Je m’oppose à recevoir des offres commerciales relatives aux
                  produits et services commercialisés par l’établissement
                  vendeur.
                </FieldLabel>
              </Field>
            </FieldGroup>
          </div>

          {/* Seller Identification */}
          <div className="space-y-4 border-t pt-4">
            <Label className="text-base font-semibold">
              Identification du vendeur (LEMA)
            </Label>

            <Field>
              <FieldLabel htmlFor="salesPerson">Vendeur *</FieldLabel>
              <FieldContent>
                {lemaSalesPersonsQuery.isPending ? (
                  <Skeleton className="h-9 w-full max-w-md" />
                ) : showMissingVehicleKvps ? (
                  <Alert variant="destructive">
                    <AlertCircleIcon className="h-4 w-4" />
                    <AlertDescription>
                      {missingVehicleKvpsMessage}
                    </AlertDescription>
                  </Alert>
                ) : showSellerQueryError ? (
                  <Alert variant="destructive">
                    <AlertCircleIcon className="h-4 w-4" />
                    <AlertDescription>
                      Une erreur est survenue lors du chargement des vendeurs
                      LEMA. Veuillez réessayer plus tard.
                    </AlertDescription>
                  </Alert>
                ) : showSellerEmptyList ? (
                  <Alert variant="destructive">
                    <AlertCircleIcon className="h-4 w-4" />
                    <AlertDescription>
                      {emptySellerAlertMessage}
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Combobox
                    items={sellerOptions}
                    itemToStringLabel={(row) => formatLemaSellerLabel(row)}
                    value={salesPerson}
                    onValueChange={(next) => setSalesPersonId(next?.id ?? null)}
                  >
                    <ComboboxInput
                      id="salesPerson"
                      placeholder="Rechercher un vendeur…"
                      showClear={salesPerson !== null}
                      aria-label="Rechercher un vendeur LEMA"
                      disabled={sellerOptions.length === 0}
                      className="max-w-md"
                    />
                    <ComboboxContent>
                      <ComboboxEmpty>Aucun vendeur trouvé.</ComboboxEmpty>
                      <ComboboxList>
                        {(row) => (
                          <ComboboxItem key={row.id} value={row}>
                            {formatLemaSellerLabel(row)}
                          </ComboboxItem>
                        )}
                      </ComboboxList>
                    </ComboboxContent>
                  </Combobox>
                )}
              </FieldContent>
              <FieldDescription className="text-sm text-gray-500">
                Identifiant LEMA du vendeur responsable de cette transaction
              </FieldDescription>
            </Field>

            <Alert className="border-blue-200 bg-blue-50">
              <InfoIcon className="h-4 w-4" />
              <AlertDescription>
                Le code LEMA permet de lier le vendeur au dossier pour le suivi
                des commissions et statistiques de vente.
              </AlertDescription>
            </Alert>
          </div>

          {/* Lead time circular */}
          <div className="space-y-2 border-t pt-4">
            <Label className="flex items-center gap-2 text-base font-semibold">
              Circulaire des délais
            </Label>

            <Alert>
              <InfoIcon className="h-4 w-4" />
              <AlertDescription>
                Pensez à vérifier la circulaire des délais
              </AlertDescription>
            </Alert>

            <div className="px-4 py-3">
              <p className="text-sm font-medium">
                Semaine de production souhaitée :
              </p>

              <p className="flex items-center gap-2 text-2xl font-bold tracking-tight">
                <Calendar1Icon />
                {productionWeekLabel}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
